import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Label } from '@playerbase/ui';
import {
  GAME_PLATFORM_LABELS,
  GAME_PLATFORM_ORDER,
  type GamePlatform,
  type PublishingDestination,
  type PublishingStore,
} from '@playerbase/types';

import { useShell } from '../../shell/use-shell.js';
import { updateGame } from '../../games/game-data.js';
import {
  addPublishingDestination,
  fetchPublishingDestinations,
  removePublishingDestination,
  updatePublishingDestinationUrl,
} from '../../games/publishing-destinations-data.js';
import { DevStagesSection } from './dev-stages/dev-stages-section.js';
import { PlatformSection } from './platform-section.js';
import { PlatformToggle } from './platform-toggle.js';

export function GameSettingsOverviewPage(): JSX.Element {
  const { activeGame, games, refresh } = useShell();
  const [name, setName] = useState(activeGame?.name ?? '');
  const [platforms, setPlatforms] = useState<GamePlatform[]>(
    activeGame?.targetPlatforms ?? [],
  );
  const [saving, setSaving] = useState(false);
  const [destinations, setDestinations] = useState<PublishingDestination[]>([]);
  const [destinationsLoading, setDestinationsLoading] = useState(false);

  // Reset local form state when the active game changes (e.g. via switcher).
  useEffect(() => {
    setName(activeGame?.name ?? '');
    setPlatforms(activeGame?.targetPlatforms ?? []);
  }, [activeGame?.id, activeGame?.name, activeGame?.targetPlatforms]);

  // Load destinations whenever the active game changes.
  useEffect(() => {
    if (activeGame === null) {
      setDestinations([]);
      return;
    }
    let cancelled = false;
    setDestinationsLoading(true);
    void fetchPublishingDestinations(activeGame.id).then((rows) => {
      if (!cancelled) setDestinations(rows);
      if (!cancelled) setDestinationsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [activeGame?.id]);

  const sortedPlatforms = useMemo(
    () => GAME_PLATFORM_ORDER.filter((p) => platforms.includes(p)),
    [platforms],
  );

  const destinationsByPlatform = useMemo(() => {
    const map = new Map<GamePlatform, PublishingDestination[]>();
    for (const dest of destinations) {
      const list = map.get(dest.platform) ?? [];
      list.push(dest);
      map.set(dest.platform, list);
    }
    return map;
  }, [destinations]);

  const togglePlatform = useCallback(
    (platform: GamePlatform): void => {
      setPlatforms((prev) => {
        if (prev.includes(platform)) {
          const attached = (destinationsByPlatform.get(platform) ?? []).length;
          if (attached > 0) {
            const ok = window.confirm(
              `Remove ${GAME_PLATFORM_LABELS[platform]}? ${attached} publishing destination${attached === 1 ? '' : 's'} attached to it will be unlinked but kept until you remove them.`,
            );
            if (!ok) return prev;
          }
          return prev.filter((p) => p !== platform);
        }
        return [...prev, platform];
      });
    },
    [destinationsByPlatform],
  );

  const dirtyName = activeGame !== null && name.trim() !== activeGame.name;
  const dirtyPlatforms =
    activeGame !== null &&
    JSON.stringify([...platforms].sort()) !==
      JSON.stringify([...activeGame.targetPlatforms].sort());
  const dirty = dirtyName || dirtyPlatforms;

  const handleSave = useCallback(async (): Promise<void> => {
    if (activeGame === null) return;
    if (!dirty) return;

    const trimmed = name.trim();
    if (trimmed.length === 0) {
      window.alert('Game name cannot be empty.');
      return;
    }

    if (dirtyName) {
      const duplicate = games.find(
        (g) => g.id !== activeGame.id && g.name.toLowerCase() === trimmed.toLowerCase(),
      );
      if (duplicate !== undefined) {
        const ok = window.confirm(
          `Another game on this studio is already called "${duplicate.name}". Save anyway?`,
        );
        if (!ok) return;
      }
    }

    setSaving(true);
    try {
      const patch: Parameters<typeof updateGame>[1] = {};
      if (dirtyName) patch.name = trimmed;
      if (dirtyPlatforms) patch.targetPlatforms = platforms;
      await updateGame(activeGame.id, patch);
      await refresh();
    } finally {
      setSaving(false);
    }
  }, [activeGame, dirty, dirtyName, dirtyPlatforms, games, name, platforms, refresh]);

  const handleAddStore = useCallback(
    async (platform: GamePlatform, store: PublishingStore): Promise<void> => {
      if (activeGame === null) return;
      const created = await addPublishingDestination({
        gameId: activeGame.id,
        platform,
        store,
      });
      if (created !== null) {
        setDestinations((prev) => [...prev, created]);
      }
    },
    [activeGame],
  );

  const handleRemoveDestination = useCallback(
    async (destination: PublishingDestination): Promise<void> => {
      const ok = await removePublishingDestination(destination.id);
      if (ok) {
        setDestinations((prev) => prev.filter((d) => d.id !== destination.id));
      }
    },
    [],
  );

  const handleUpdateUrl = useCallback(
    async (
      destination: PublishingDestination,
      storeUrl: string | null,
    ): Promise<void> => {
      const updated = await updatePublishingDestinationUrl(destination.id, storeUrl);
      if (updated !== null) {
        setDestinations((prev) =>
          prev.map((d) => (d.id === updated.id ? updated : d)),
        );
      }
    },
    [],
  );

  if (activeGame === null) {
    return (
      <div className="px-6 py-10 text-sm text-text-muted">
        Select a game to edit its overview.
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-6 py-6">
      <header className="mb-6">
        <h1 className="font-display text-2xl font-bold text-text">Game overview</h1>
        <p className="mt-1 text-sm text-text-muted">
          The foundational configuration for {activeGame.name} - its name and the
          platforms it ships to. Drives milestone scoping and the publishing
          destinations available below.
        </p>
      </header>

      <div className="space-y-6">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="game-name">Game name</Label>
          <Input
            id="game-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div>
          <Label>Platforms</Label>
          <p className="mt-1 mb-2 text-xs text-text-muted">
            Select every platform this game ships to. Platforms can be added or
            removed later.
          </p>
          <PlatformToggle
            selected={platforms}
            onToggle={togglePlatform}
            disabled={saving}
          />
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            size="md"
            disabled={!dirty || saving}
            onClick={() => void handleSave()}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
          {dirty && !saving && (
            <span className="text-xs text-text-muted">Unsaved changes</span>
          )}
        </div>
      </div>

      <DevStagesSection />

      {sortedPlatforms.length > 0 && (
        <div className="mt-10 space-y-4">
          <header>
            <h2 className="font-display text-lg font-semibold text-text">
              Publishing destinations
            </h2>
            <p className="mt-1 text-xs text-text-muted">
              Capture the stores this game ships on for each platform.
              {destinationsLoading && ' Loading…'}
            </p>
          </header>
          {sortedPlatforms.map((platform) => (
            <PlatformSection
              key={platform}
              platform={platform}
              destinations={destinationsByPlatform.get(platform) ?? []}
              onAddStore={handleAddStore}
              onRemove={handleRemoveDestination}
              onUpdateUrl={handleUpdateUrl}
            />
          ))}
        </div>
      )}
      </div>
    </div>
  );
}
